import { httpsCallable } from 'firebase/functions';
import { useEffect, useRef, useState } from 'react';

import { fns } from '@/integrations/firebase/functions';

// A single Polish product (brand) registered under a group's ATC code.
type DrugSuggestionProduct = {
  id: string;
  name: string;
  moc: string | null;
  substances: string[];
};

// One active substance of a combination product, annotated with the mono ATC a
// search would actually run for (null when the substance has no standalone
// product in RPL — the UI then submits the substance name and lets RxNorm map it).
type DrugSuggestionComponent = {
  substance: string;
  atc: string | null;
};

// A suggestion group anchored on an ATC code (the molecule/route). Selecting any
// part of the group commits its `atc`; combinations carry `components` so the
// user can pick which substance to analyse.
export type DrugSuggestionGroup = {
  atc: string;
  substance: string;
  substances: string[];
  is_combination: boolean;
  products: DrugSuggestionProduct[];
  total_products: number;
  // Predominant route of administration (normalised Polish adjective, e.g.
  // "doustny", "na skórę", "dożylny"), used to tell same-substance groups apart.
  // Null when no route could be derived.
  route?: string | null;
  components?: DrugSuggestionComponent[];
  // Combinations only: false when the mixture has more active substances than the
  // resolver's fan-out cap (combination_max_components), so a joint report would
  // never generate. The UI then offers only per-ingredient picks — no dead-end
  // "analyse together" action. Absent / undefined on mono groups (treated as true).
  reportable_together?: boolean;
  // Set when the group surfaced because of a brand name rather than the visible
  // substance label — the actual matched brand, shown inline so the row is
  // self-explanatory (Journey B). Absent when the label itself matched.
  matched_brand?: string;
  // Tri-state library membership from the Postgres alias mirror: true means a
  // ready report is known, false means a successful lookup confirmed no report,
  // and absent means availability could not be established. `drug_id` and
  // `matched_alias` are present only when the typed query itself matched an alias.
  in_library?: boolean;
  drug_id?: string;
  matched_alias?: string;
};

type SuggestResponse = {
  groups: DrugSuggestionGroup[];
};

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

export function useDrugSuggestions(query: string, enabled = true) {
  const trimmed = query.trim();
  const active = enabled && trimmed.length >= MIN_QUERY_LENGTH;
  const [groups, setGroups] = useState<DrugSuggestionGroup[]>([]);
  const [loading, setLoading] = useState(active);
  // Distinct from "empty results": the callable threw (offline / backend error),
  // so the dropdown can show a retryable error state instead of silent guidance.
  const [error, setError] = useState(false);
  // Bumped by retry() to re-run the effect for the same query after a failure.
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);
  const callableRef = useRef(
    httpsCallable<{ q: string; limit?: number }, SuggestResponse>(
      fns,
      'drug_suggest',
    ),
  );

  // Synchronous state resets happen during render (React's derived-state
  // pattern), not in the effect: a new active query marks loading and clears
  // the error while the previous groups stay on screen (stale-while-revalidate);
  // dropping below the minimum / disabling clears everything.
  const resetKey = `${active ? '1' : '0'}${trimmed}${reloadKey}`;
  const [trackedKey, setTrackedKey] = useState(resetKey);
  if (resetKey !== trackedKey) {
    setTrackedKey(resetKey);
    setError(false);
    if (active) {
      setLoading(true);
    } else {
      setGroups([]);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!active) return;

    const requestId = ++requestIdRef.current;

    const timer = window.setTimeout(async () => {
      try {
        const result = await callableRef.current({ q: trimmed, limit: 8 });
        if (requestId !== requestIdRef.current) {
          return;
        }
        setGroups(result.data.groups ?? []);
        setError(false);
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        console.error('Drug suggestion fetch failed:', err);
        setGroups([]);
        setError(true);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [active, trimmed, reloadKey]);

  const retry = () => setReloadKey((k) => k + 1);

  return { groups, loading, error, retry };
}
